import EvaluationEditModal from 'components/modal/evaluations/EvaluationEditModal';
import ReplacementAssociateStudentModal from 'components/modal/evaluations/ReplacementAssociateStudentModal';
import ReplacementEditModal from 'components/modal/evaluations/ReplacementEditModal';
import {
  Button,
  Empty,
  Flex,
  Modal,
  Pagination,
  Skeleton,
  Typography
} from 'antd';
import { CrudFilters, HttpError, useDelete } from '@refinedev/core';
import { EvaluationsListItem } from './EvaluationsListItem';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { ModalCreateEvaluation } from './ModalCreateEvaluation/ModalCreateEvaluation';
import { ReplacementGroup } from 'components/professor_area/ReplacementGroup';
import { useCallback, useMemo, useState } from 'react';
import { useCanMultiple } from 'components/layout/sider';
import { useModal, useModalForm, useSelect } from '@refinedev/antd';
import { useParams } from 'react-router-dom';
import type { IAssessment } from 'interfaces/assessments';
import type {
  IProfessorAreaEvaluationAssessmentCreate,
  IProfessorAreaEvaluationsList
} from 'interfaces/professor_area/evaluations';
import type { TableProps } from 'antd/lib';

interface EvaluationsListProps {
  tableProps: TableProps<IProfessorAreaEvaluationsList>;
  onChangePage: (page: number) => void;
  refetch: () => void;
  disciplineId: string;
}

export const EvaluationsList = ({
  tableProps,
  onChangePage,
  refetch,
  disciplineId
}: EvaluationsListProps) => {
  const params = useParams();
  const classroomId = params.id!;
  const resource = 'professor_area/classroom_assessments';
  const meta = useMemo(() => ({ classroomId }), [classroomId]);
  const permissions = useCanMultiple([
    { resource, action: 'create' },
    { resource, action: 'show' },
    { resource, action: 'edit' },
    { resource, action: 'delete' },
    { resource: 'professor_area/classroom_enrollments', action: 'show' }
  ]);
  const canCreate = Boolean(permissions[0]?.data?.can);
  const canShow = Boolean(permissions[1]?.data?.can);
  const canEdit = Boolean(permissions[2]?.data?.can);
  const canDelete = Boolean(permissions[3]?.data?.can);
  const canShowEnrollments = Boolean(permissions[4]?.data?.can);

  const { pagination, dataSource: items, loading } = tableProps;
  const { confirm } = Modal;

  const [editingEvaluation, setEditingEvaluation] =
    useState<IProfessorAreaEvaluationsList | null>(null);

  const { mutateAsync: deleteEvaluation } =
    useDelete<IProfessorAreaEvaluationsList>();

  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createModalShow,
    close: createModalClose,
    formLoading
  } = useModalForm<
    IProfessorAreaEvaluationAssessmentCreate,
    HttpError,
    IProfessorAreaEvaluationAssessmentCreate
  >({
    resource,
    meta,
    action: 'create',
    redirect: false,
    autoResetForm: true,
    onMutationSuccess: () => {
      onChangePage(1);
    },
    successNotification: () => ({
      type: 'success',
      message: 'Avaliação cadastrada com sucesso',
      description: 'Sucesso!'
    }),
    errorNotification: () => ({
      type: 'error',
      message: 'Não foi possível cadastrar a avaliação. Tente novamente!',
      description: 'Algo deu errado!'
    })
  });

  const [replacementParent, setReplacementParent] =
    useState<IProfessorAreaEvaluationsList | null>(null);

  const {
    modalProps: replacementModalProps,
    formProps: replacementFormProps,
    show: replacementModalShow,
    close: replacementModalCloseInternal,
    formLoading: replacementFormLoading
  } = useModalForm<
    IProfessorAreaEvaluationAssessmentCreate,
    HttpError,
    IProfessorAreaEvaluationAssessmentCreate
  >({
    resource,
    meta,
    action: 'create',
    redirect: false,
    autoResetForm: true,
    onMutationSuccess: () => {
      onChangePage(1);
    },
    successNotification: () => ({
      type: 'success',
      message: 'Substitutiva cadastrada com sucesso',
      description: 'Sucesso!'
    }),
    errorNotification: () => ({
      type: 'error',
      message: 'Não foi possível cadastrar a substitutiva. Tente novamente!',
      description: 'Algo deu errado!'
    })
  });

  const handleAddReplacement = useCallback(
    (evaluation: IProfessorAreaEvaluationsList) => {
      setReplacementParent(evaluation);
      replacementModalShow();
    },
    [replacementModalShow]
  );

  const replacementModalClose = useCallback(() => {
    replacementModalCloseInternal();
    setReplacementParent(null);
  }, [replacementModalCloseInternal]);

  const {
    show: editModalShow,
    close: editModalClose,
    modalProps: editModalProps
  } = useModal();

  const [associatingEvaluation, setAssociatingEvaluation] =
    useState<IProfessorAreaEvaluationsList | null>(null);

  const {
    show: associateModalShow,
    close: associateModalCloseInternal,
    modalProps: associateModalProps
  } = useModal();

  const handleAssociateStudents = useCallback(
    (evaluation: IProfessorAreaEvaluationsList) => {
      setAssociatingEvaluation(evaluation);
      associateModalShow();
    },
    [associateModalShow]
  );

  const associateModalClose = useCallback(() => {
    associateModalCloseInternal();
    setAssociatingEvaluation(null);
  }, [associateModalCloseInternal]);

  const filters: CrudFilters = [
    {
      field: 'filter[by_parent_type]',
      operator: 'eq',
      value: 'Graduate::Discipline'
    },
    {
      field: 'filter[by_parent_id]',
      operator: 'eq',
      value: disciplineId
    }
  ];

  const { selectProps: assessmentSelectProps } = useSelect<
    IAssessment,
    HttpError
  >({
    resource: 'assessments',
    optionLabel: 'name',
    optionValue: 'id',
    filters,
    onSearch: value => [
      {
        field: 'filter[search_name]',
        operator: 'eq',
        value
      }
    ],
    pagination: {
      mode: 'server'
    },
    queryOptions: {
      enabled: canCreate || canEdit
    }
  });

  const handleEdit = useCallback(
    (evaluation: IProfessorAreaEvaluationsList) => {
      setEditingEvaluation(evaluation);
      editModalShow();
    },
    [editModalShow]
  );

  const handleEditClose = useCallback(() => {
    editModalClose();
    setEditingEvaluation(null);
  }, [editModalClose]);

  const handleRemoveEvaluation = useCallback(
    (evaluationId: string) => {
      confirm({
        title: 'Deseja excluir essa avaliação?',
        icon: <ExclamationCircleOutlined className="!text-red-500" />,
        content: 'Essa ação não pode ser revertida',
        okText: 'Excluir',
        cancelText: 'Voltar',
        okButtonProps: {
          danger: true
        },
        onOk() {
          deleteEvaluation({
            id: evaluationId,
            resource,
            meta,
            successNotification: () => ({
              type: 'success',
              description: 'Sucesso!',
              message: 'Avaliação excluída com sucesso.'
            }),
            errorNotification: () => ({
              type: 'error',
              description: 'Ops!',
              message:
                'Não foi possível excluir esta avaliação. Tente novamente.'
            })
          }).then(() => {
            refetch();
          });
        }
      });
    },
    [confirm, deleteEvaluation, meta, refetch, resource]
  );

  const hasData = useMemo(() => {
    return !loading && items && items?.length > 0;
  }, [items, loading]);

  if (loading) {
    return (
      <Skeleton active paragraph={{ rows: 6 }} className="w-full h-full" />
    );
  }

  const EmptyComponent = () => {
    return (
      <Flex
        vertical
        gap={16}
        justify="center"
        align="center"
        className="h-full"
      >
        <Empty description="Nenhuma avaliação encontrada" />

        {canCreate && (
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => createModalShow()}
          >
            Adicionar avaliação
          </Button>
        )}
      </Flex>
    );
  };

  const ContentComponent = () => {
    return (
      <>
        <Flex justify="space-between" align="center" className="mb-4">
          <Typography.Title level={5}>Avaliações da turma</Typography.Title>
          {canCreate && (
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => createModalShow()}
            >
              Adicionar avaliação
            </Button>
          )}
        </Flex>

        <Flex vertical>
          {items?.map(evaluation => (
            <ReplacementGroup
              key={evaluation.id}
              item={evaluation}
              renderItem={(item, role) => (
                <EvaluationsListItem
                  evaluation={item}
                  onEdit={handleEdit}
                  onRemove={handleRemoveEvaluation}
                  onAddReplacement={handleAddReplacement}
                  onAssociateStudents={handleAssociateStudents}
                  canShow={canShow}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  canCreate={canCreate}
                  canAssociateStudents={canShowEnrollments}
                  className={role === 'parent' ? '!border-t-0' : undefined}
                />
              )}
            />
          ))}
        </Flex>

        <Pagination
          className="justify-self-end mt-4"
          hideOnSinglePage
          {...pagination}
          onChange={onChangePage}
        />
      </>
    );
  };

  return (
    <>
      {hasData ? <ContentComponent /> : <EmptyComponent />}

      {createModalProps.open && (
        <ModalCreateEvaluation
          modalProps={createModalProps}
          formProps={createFormProps}
          classroomId={classroomId}
          isLoading={formLoading}
          onCloseModal={createModalClose}
          assessmentSelectProps={assessmentSelectProps}
        />
      )}

      {replacementModalProps.open && replacementParent && (
        <ModalCreateEvaluation
          modalProps={replacementModalProps}
          formProps={replacementFormProps}
          classroomId={classroomId}
          isLoading={replacementFormLoading}
          onCloseModal={replacementModalClose}
          assessmentSelectProps={assessmentSelectProps}
          replacementParent={replacementParent}
        />
      )}

      {editingEvaluation && editingEvaluation.kind === 'replacement' && (
        <ReplacementEditModal
          modalProps={editModalProps}
          modalClose={handleEditClose}
          evaluationResource={resource}
          assignmentSelectorProps={{ options: [] }}
          assessmentSelectorProps={assessmentSelectProps}
          evaluationId={editingEvaluation.id}
          parentId={editingEvaluation.parent_id}
        />
      )}

      {editingEvaluation && editingEvaluation.kind !== 'replacement' && (
        <EvaluationEditModal
          modalProps={editModalProps}
          modalClose={handleEditClose}
          evaluationResource={resource}
          resourceMeta={meta}
          assignmentSelectorProps={{ options: [] }}
          assessmentSelectorProps={assessmentSelectProps}
          evaluationId={editingEvaluation.id}
          onUpdateSuccess={refetch}
        />
      )}

      {associateModalProps.open && associatingEvaluation && (
        <ReplacementAssociateStudentModal
          modalProps={associateModalProps}
          modalClose={associateModalClose}
          parentId={associatingEvaluation.id}
          resource="professor_area/classroom_enrollments"
          resourceMeta={{
            classroomId,
            pathTemplate: 'professor_area/classrooms/:classroomId/enrollments'
          }}
          enrollmentResource="professor_area/classroom_assessments"
          enrollmentResourceMeta={{
            classroomId,
            evaluationId: associatingEvaluation.id,
            pathTemplate:
              'professor_area/classrooms/:classroomId/evaluations/:evaluationId/enrollments'
          }}
          optionLabel="student_name"
          searchFilterField="filter[by_user_name]"
        />
      )}
    </>
  );
};
